import { takeRouteLead, finishRouteLead } from '../data/routeLead';
import { placePicker } from './lead/placePicker';
import { manualPoint } from './lead/manualPoint';
import { STAGES } from '../logic/leadStages';
import type { BusinessType, Heat, Language, OwnerRole, PosSystem, Stage } from '../../../shared/enums';
import { BUSINESS_TYPES, HEATS, LANGUAGES, OWNER_ROLES, POS_SYSTEMS } from '../../../shared/enums';
import type { LeadField } from '../../../shared/fields';
import type { Lead } from '../../../shared/types';
import { shell } from '../app';
import { createLead, deleteLead, getLead, listLeads, restoreLead, updateLead } from '../data/store';
import type { LeadPatch } from '../data/store';
import { leadFormErrors } from '../logic/leadValidation';
import type { LeadFormError, LeadFormField } from '../logic/leadValidation';
import { btn, field, input, segmented, select, textarea } from '../ui/components';
import { h } from '../ui/dom';
import {
  BUSINESS_TYPE_LABELS,
  HEAT_LABELS,
  LANGUAGE_LABELS,
  OWNER_ROLE_LABELS,
  POS_LABELS,
  STAGE_LABELS,
} from '../ui/labels';
import { navigate } from '../ui/router';
import type { View } from '../ui/router';

type Draft = Pick<Lead, LeadField>;

const EMPTY: Draft = {
  business_name: '',
  business_type: 'restaurant',
  address: null,
  address_city: null,
  address_postal_code: null,
  place_id: null,
  neighborhood: null,
  owner_name: null,
  owner_role: null,
  owner_language: 'en',
  phone: null,
  email: null,
  instagram: null,
  current_pos: 'unknown',
  pos_monthly_cost: null,
  contract_end_date: null,
  etf_amount: null,
  stage: 'new',
  heat: 'warm',
  best_visit_window: null,
  notes: null,
  meeting_on: null,
  demo_on: null,
  application_on: null,
  activation_on: null,
  gpv_usd: null,
  gpv_as_of: null,
};

const str = (v: string): string | null => (v.trim() ? v.trim() : null);
/** Match the API's zod bounds (0 … 1,000,000) so a save can never be server-rejected. */
const num = (v: string): number | null => {
  if (v.trim() === '') return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.min(1_000_000, Math.max(0, n));
};

/** Fields that changed between two drafts (null-safe). */
const diff = (a: Draft, b: Draft): LeadPatch =>
  Object.fromEntries(
    (Object.keys(b) as (keyof Draft)[]).filter((k) => a[k] !== b[k]).map((k) => [k, b[k]]),
  ) as LeadPatch;

export const leadFormView: View = async ({ root, params, query }) => {
  shell.setChrome(true);
  const id = params.id;
  const existing = id ? await getLead(id) : undefined;
  if (id && !existing) {
    shell.setTitle('Edit lead', { back: '#/pipeline' });
    root.appendChild(h('p', { class: 'empty__text' }, 'Lead not found on this device.'));
    return;
  }
  const routeLead = !id ? takeRouteLead(query.get('routePlace')) : undefined;
  const original: Draft = existing ? { ...EMPTY, ...existing } : {
    ...EMPTY,
    ...(routeLead ? {
      place_id: routeLead.place_id,
      business_type: routeLead.business_type,
      current_pos: routeLead.current_pos,
    } : {}),
  };
  let created: Lead | undefined;
  let draft: Draft = { ...original };
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => {
    draft = { ...draft, [k]: v };
  };
  shell.setTitle(existing ? 'Edit lead' : 'New lead', {
    back: existing ? `#/leads/${encodeURIComponent(existing.id)}` : routeLead ? '#/route' : '#/pipeline',
  });

  const nameInput = input({
    value: draft.business_name,
    maxlength: 120,
    placeholder: 'Business name',
    autocapitalize: 'words',
    required: true,
    onInput: (v) => {
      nameInput.setCustomValidity('');
      set('business_name', v);
    },
  });
  // Inputs the pre-save check can point at; typing clears the flag.
  const emailInput = input({
    value: draft.email,
    maxlength: 120,
    type: 'email',
    inputmode: 'email',
    placeholder: 'owner@business.com',
    autocapitalize: 'none',
    onInput: (v, el) => {
      el.setCustomValidity('');
      set('email', str(v));
    },
  });
  const contractInput = input({
    value: draft.contract_end_date,
    type: 'date',
    onInput: (v, el) => {
      el.setCustomValidity('');
      set('contract_end_date', v || null);
    },
  });
  const picker = placePicker(draft.place_id, existing?.id, value => set('place_id', value));
  const placement = manualPoint();
  const checkedInputs: Record<LeadFormField, HTMLInputElement> = {
    email: emailInput,
    place_id: picker.query,
    contract_end_date: contractInput,
  };
  const flagInvalid = (problem: LeadFormError) => {
    const el = checkedInputs[problem.field];
    el.setCustomValidity(problem.message);
    el.focus();
    el.reportValidity();
    shell.toast(problem.message, { kind: 'error' });
  };

  const save = btn(existing ? 'Save changes' : 'Create lead', {
    variant: 'primary',
    size: 'lg',
    class: 'w-full',
    onClick: async () => {
      if (!draft.business_name.trim()) {
        // spaces alone pass the browser's `required`: say why nothing was saved
        nameInput.setCustomValidity('Business name is required');
        nameInput.focus();
        nameInput.reportValidity();
        shell.toast('Business name is required', { kind: 'error' });
        return;
      }
      save.disabled = true;
      const clean: Draft = { ...draft, business_name: draft.business_name.trim() };
      const changes: LeadPatch = existing ? diff(original, clean) : clean;
      try {
        // Refuse what the API would reject: a rejected save is parked and vanishes after the next sync.
        const [problem] = leadFormErrors(changes, await listLeads(), existing?.id ?? created?.id);
        if (problem) {
          flagInvalid(problem);
          save.disabled = false;
          return;
        }
        if (existing) {
          if (Object.keys(changes).length) await updateLead(existing.id, changes);
          shell.toast('Saved');
          navigate(`#/leads/${encodeURIComponent(existing.id)}`, { replace: true });
        } else {
          const lead = created ?? await createLead({ ...clean, ...placement.get() });
          created = lead;
          if (routeLead) await finishRouteLead(routeLead, lead);
          shell.toast(
            placement.get()
              ? 'Saved — location confirmed'
              : navigator.onLine
                ? 'Saved; looking up location'
                : 'Saved — location needs confirmation',
          );
          navigate(routeLead
            ? `#/log?lead=${encodeURIComponent(lead.id)}&return=${encodeURIComponent('/route')}`
            : `#/leads/${encodeURIComponent(lead.id)}`, { replace: true });
        }
      } catch (err) {
        shell.toast(`Could not save: ${err instanceof Error ? err.message : String(err)}`, { kind: 'error' });
        save.disabled = false;
      }
    },
  });

  const enumSelect = <T extends string>(
    values: readonly T[],
    labels: Record<T, string>,
    value: T | null,
    onChange: (v: T) => void,
    placeholder?: string,
  ) =>
    select<T>({ options: values.map((v) => ({ value: v, label: labels[v] })), value, onChange, placeholder });

  root.appendChild(
    h(
      'div',
      { class: 'page page--form' },
      h('h2', { class: 'form__h' }, 'Business'),
      picker.root,
      field('Name', nameInput),
      field(
        'Type',
        enumSelect<BusinessType>(BUSINESS_TYPES, BUSINESS_TYPE_LABELS, draft.business_type, (v) =>
          set('business_type', v),
        ),
      ),
      field(
        'Address',
        input({
          value: draft.address,
          maxlength: 200,
          placeholder: 'Street address',
          autocapitalize: 'words',
          autocomplete: 'street-address',
          onInput: (v) => set('address', str(v)),
        }),
      ),
      field('City', input({ value: draft.address_city, maxlength: 80, onInput: (v) => set('address_city', str(v)) })),
      field('ZIP', input({ value: draft.address_postal_code, maxlength: 5, onInput: (v) => set('address_postal_code', str(v)) })),
      !existing ? placement.root : null,
      field(
        'Neighborhood',
        input({
          value: draft.neighborhood,
          maxlength: 80,
          placeholder: 'Santa Ana, Costa Mesa…',
          autocapitalize: 'words',
          onInput: (v) => set('neighborhood', str(v)),
        }),
      ),
      field(
        'Best time to visit',
        input({
          value: draft.best_visit_window,
          maxlength: 80,
          placeholder: 'e.g. 2–4pm weekdays',
          onInput: (v) => set('best_visit_window', str(v)),
        }),
      ),

      h('h2', { class: 'form__h' }, 'Owner'),
      h(
        'div',
        { class: 'grid-2' },
        field(
          'Name',
          input({
            value: draft.owner_name,
            maxlength: 80,
            placeholder: 'Owner name',
            autocapitalize: 'words',
            onInput: (v) => set('owner_name', str(v)),
          }),
        ),
        field(
          'Role',
          enumSelect<OwnerRole>(
            OWNER_ROLES,
            OWNER_ROLE_LABELS,
            draft.owner_role,
            (v) => set('owner_role', v),
            'Role',
          ),
        ),
      ),
      field(
        'Speaks',
        segmented<Language>({
          options: LANGUAGES.map((l) => ({ value: l, label: LANGUAGE_LABELS[l] })),
          value: draft.owner_language,
          onChange: (v) => set('owner_language', v),
          columns: 3,
          size: 'md',
        }),
      ),
      h(
        'div',
        { class: 'grid-2' },
        field(
          'Phone',
          input({
            value: draft.phone,
            maxlength: 40,
            type: 'tel',
            inputmode: 'tel',
            placeholder: '714…',
            onInput: (v) => set('phone', str(v)),
          }),
        ),
        field(
          'Instagram',
          input({
            value: draft.instagram,
            maxlength: 60,
            placeholder: 'handle',
            autocapitalize: 'none',
            onInput: (v) => set('instagram', str(v.replace(/^@/, ''))),
          }),
        ),
      ),
      field('Email', emailInput),

      h('h2', { class: 'form__h' }, 'Current POS'),
      field(
        'System',
        enumSelect<PosSystem>(POS_SYSTEMS, POS_LABELS, draft.current_pos, (v) => set('current_pos', v)),
      ),
      h(
        'div',
        { class: 'grid-2' },
        field(
          'Monthly cost ($)',
          input({
            value: draft.pos_monthly_cost?.toString() ?? '',
            type: 'number',
            inputmode: 'decimal',
            min: '0',
            step: '1',
            placeholder: '0',
            onInput: (v) => set('pos_monthly_cost', num(v)),
          }),
        ),
        field(
          'ETF ($)',
          input({
            value: draft.etf_amount?.toString() ?? '',
            type: 'number',
            inputmode: 'decimal',
            min: '0',
            step: '1',
            placeholder: '0',
            onInput: (v) => set('etf_amount', num(v)),
          }),
        ),
      ),
      field('Contract end date', contractInput),

      h('h2', { class: 'form__h' }, 'Pipeline'),
      h(
        'div',
        { class: 'grid-2' },
        field(
          'Stage',
          enumSelect<Stage>(STAGES, STAGE_LABELS, draft.stage, (v) => set('stage', v)),
        ),
        field(
          'Heat',
          segmented<Heat>({
            options: HEATS.map((x) => ({ value: x, label: HEAT_LABELS[x] })),
            value: draft.heat,
            onChange: (v) => set('heat', v),
            columns: 3,
            size: 'md',
          }),
        ),
      ),
      field(
        'Notes',
        textarea({
          value: draft.notes,
          maxlength: 4000,
          rows: 4,
          placeholder: 'Anything worth remembering',
          onInput: (v) => set('notes', str(v)),
        }),
      ),


      existing
        ? h(
            'div',
            { class: 'dangerzone' },
            btn('Delete lead', {
              variant: 'danger',
              // Undo instead of a confirm dialog: one tap, and a slip is recoverable.
              onClick: async (ev) => {
                const button = ev.currentTarget as HTMLButtonElement;
                button.disabled = true; // one tap, one DELETE
                let archive;
                try {
                  archive = await deleteLead(existing.id);
                } catch (err) {
                  shell.toast(`Could not delete: ${err instanceof Error ? err.message : String(err)}`, {
                    kind: 'error',
                  });
                  button.disabled = false;
                  return;
                }
                navigate('#/pipeline', { replace: true });
                if (!archive) return;
                shell.toast(`Deleted “${existing.business_name}” — tap to undo`, {
                  duration: 8000,
                  onClick: () =>
                    void restoreLead(archive)
                      .then(() => navigate(`#/leads/${encodeURIComponent(archive.lead.id)}`))
                      .catch((err: unknown) =>
                        shell.toast(`Could not undo: ${err instanceof Error ? err.message : String(err)}`, {
                          kind: 'error',
                        }),
                      ),
                });
              },
            }),
          )
        : null,
      h('div', { class: 'actionbar' }, save),
    ),
  );
  const focus = !existing ? setTimeout(() => nameInput.focus(), 30) : undefined;
  return () => { clearTimeout(focus); picker.dispose(); };
};
